import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_CARD,
  BG_PAGE,
  FONT_HEADING,
} from '../components/legacy-ball/table-management/designTokens';

// ── Types ───────────────────────────────────────────────────

type SubView = 'stories' | 'questions' | 'vocabulary';

interface Story {
  id: string;
  title: string;
  guestName: string;
  status: string;
  chapterAffiliation?: string;
  familyName?: string;
  reunionYear: number;
  featured: boolean;
  createdAt: string;
  approvedAt?: string;
}

interface StoriesResponse {
  data: Story[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface StoryQuestion {
  id: string;
  text: string;
  category: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

interface HeritageVocabulary {
  id: string;
  englishTerm: string;
  krioTerm: string;
  definition: string;
  category: string;
  source: string;
  pronunciation?: string;
  culturalNotes?: string;
  verificationStatus: string;
  verifiedById?: string;
  verifiedAt?: string;
  createdAt: string;
  updatedAt: string;
}

const QUESTION_CATEGORIES = [
  'FamilyHeritage',
  'FreetownMemories',
  'KrioLanguage',
  'FoodTraditions',
  'FaithValues',
  'KDUExperience',
  'LegacyFutureGenerations',
];

const VERIFICATION_STATUSES = ['NeedsReview', 'Verified', 'Questionable', 'DuplicateCandidate'];
const STORY_STATUSES = ['Generated', 'Approved', 'Declined', 'Flagged', 'Removed'];

// ── Styles ──────────────────────────────────────────────────

const iconBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px 6px',
  fontSize: '0.9rem',
  borderRadius: '4px',
  lineHeight: 1,
  transition: 'background-color 0.15s',
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: '1.5rem',
    maxWidth: '1200px',
    margin: '0 auto',
    backgroundColor: BG_PAGE,
    minHeight: '100vh',
  },
  title: {
    fontFamily: FONT_HEADING,
    fontSize: '1.5rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: '0 0 1rem 0',
  },
  subNav: {
    display: 'flex',
    gap: '0',
    borderBottom: `2px solid ${BORDER_LIGHT}`,
    marginBottom: '1.5rem',
  },
  subTab: {
    padding: '10px 20px',
    fontSize: '0.9rem',
    fontWeight: 500,
    color: TEXT_MUTED,
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    marginBottom: '-2px',
    transition: 'color 0.15s, border-color 0.15s',
  },
  subTabActive: {
    padding: '10px 20px',
    fontSize: '0.9rem',
    fontWeight: 600,
    color: PRIMARY,
    background: 'none',
    border: 'none',
    borderBottom: `2px solid ${PRIMARY}`,
    cursor: 'pointer',
    marginBottom: '-2px',
  },
  filterBar: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap' as const,
    marginBottom: '1rem',
    alignItems: 'center',
  },
  input: {
    padding: '8px 12px',
    fontSize: '0.88rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    outline: 'none',
  },
  select: {
    padding: '8px 12px',
    fontSize: '0.88rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    outline: 'none',
    backgroundColor: BG_CARD,
  },
  card: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '1.25rem',
    marginBottom: '1rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.88rem',
  },
  th: {
    textAlign: 'left' as const,
    padding: '10px 12px',
    borderBottom: `2px solid ${BORDER_LIGHT}`,
    fontWeight: 600,
    color: TEXT_DARK,
    fontSize: '0.82rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
  },
  td: {
    padding: '10px 12px',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    color: TEXT_DARK,
  },
  badge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  btnPrimary: {
    padding: '8px 16px',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#fff',
    backgroundColor: PRIMARY,
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  btnSecondary: {
    padding: '8px 16px',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#333',
    backgroundColor: '#e0e0e0',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: TEXT_MUTED,
    fontSize: '0.9rem',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    fontSize: '0.85rem',
    color: TEXT_MUTED,
  },
  formRow: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap' as const,
    alignItems: 'flex-end',
    marginBottom: '1rem',
  },
  formField: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  label: {
    fontSize: '0.78rem',
    fontWeight: 600,
    color: TEXT_MUTED,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
  },
  textarea: {
    padding: '8px 12px',
    fontSize: '0.88rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    outline: 'none',
    resize: 'vertical' as const,
    minHeight: '60px',
    fontFamily: 'inherit',
  },
  errorText: {
    color: '#d32f2f',
    fontSize: '0.82rem',
    marginTop: '4px',
  },
};

// ── Helpers ─────────────────────────────────────────────────

const STATUS_BADGE_COLORS: Record<string, { bg: string; color: string }> = {
  Generated: { bg: '#e3f2fd', color: '#1565c0' },
  Approved: { bg: '#e8f5e9', color: '#2e7d32' },
  Declined: { bg: '#eceff1', color: '#546e7a' },
  Flagged: { bg: '#fff3e0', color: '#e65100' },
  Removed: { bg: '#ffebee', color: '#c62828' },
};

const CATEGORY_LABELS: Record<string, string> = {
  FamilyHeritage: 'Family & Heritage',
  FreetownMemories: 'Freetown Memories',
  KrioLanguage: 'Krio Language',
  FoodTraditions: 'Food & Traditions',
  FaithValues: 'Faith & Values',
  KDUExperience: 'KDU Experience',
  LegacyFutureGenerations: 'Legacy & Future',
};

const VERIFICATION_BADGE_COLORS: Record<string, { bg: string; color: string }> = {
  NeedsReview: { bg: '#fff3e0', color: '#e65100' },
  Verified: { bg: '#e8f5e9', color: '#2e7d32' },
  Questionable: { bg: '#ffebee', color: '#c62828' },
  DuplicateCandidate: { bg: '#eceff1', color: '#546e7a' },
};

function StatusBadge({ status, colors }: { status: string; colors: Record<string, { bg: string; color: string }> }) {
  const c = colors[status] || { bg: '#eceff1', color: '#546e7a' };
  return (
    <span style={{ ...styles.badge, backgroundColor: c.bg, color: c.color }}>
      {status}
    </span>
  );
}

// ── Stories Sub-Tab ─────────────────────────────────────────

function StoriesTab({ isAdmin }: { isAdmin: boolean }) {
  const [stories, setStories] = useState<Story[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filters
  const [chapter, setChapter] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [reunionYear, setReunionYear] = useState('');
  const [status, setStatus] = useState('');

  // Flag modal
  const [flagTarget, setFlagTarget] = useState<string | null>(null);
  const [flagReason, setFlagReason] = useState('');

  // Export selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const fetchStories = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (chapter) params.set('chapter', chapter);
      if (familyName) params.set('familyName', familyName);
      if (reunionYear) params.set('reunionYear', reunionYear);
      if (status && isAdmin) params.set('status', status);
      params.set('page', String(page));
      const res = await apiClient<StoriesResponse>(`/story-builder/stories?${params.toString()}`);
      setStories(res.data);
      setTotal(res.total);
      setTotalPages(res.totalPages);
    } catch (e: any) {
      setError(e.message || 'Failed to load stories');
    } finally {
      setLoading(false);
    }
  }, [chapter, familyName, reunionYear, status, page, isAdmin]);

  useEffect(() => { fetchStories(); }, [fetchStories]);

  const handleFeatureToggle = async (id: string) => {
    try {
      await apiClient(`/story-builder/stories/${id}/feature`, { method: 'PATCH' });
      fetchStories();
    } catch (e: any) {
      setError(e.message || 'Failed to toggle featured');
    }
  };

  const handleRetranslate = async (id: string, title: string) => {
    try {
      const result = await apiClient<{ promptVersion?: string; translation?: { confidenceScore?: number } }>(`/story-builder/stories/${id}/retranslate`, { method: 'POST' });
      setError('');
      const version = result?.promptVersion || 'unknown';
      const confidence = result?.translation?.confidenceScore ?? '?';
      alert(`✓ Translation regenerated for "${title}"\n\nPrompt version: ${version}\nConfidence: ${confidence}%`);
    } catch (e: any) {
      setError(e.message || 'Failed to retranslate story');
    }
  };

  const handleModerate = async (id: string, action: 'approve' | 'flag' | 'remove') => {
    if (action === 'flag') {
      setFlagTarget(id);
      return;
    }
    try {
      await apiClient(`/story-builder/stories/${id}/moderate`, {
        method: 'PATCH',
        body: { action },
      });
      fetchStories();
    } catch (e: any) {
      setError(e.message || 'Failed to moderate story');
    }
  };

  const submitFlag = async () => {
    if (!flagTarget || !flagReason.trim()) return;
    try {
      await apiClient(`/story-builder/stories/${flagTarget}/moderate`, {
        method: 'PATCH',
        body: { action: 'flag', flagReason: flagReason.trim() },
      });
      setFlagTarget(null);
      setFlagReason('');
      fetchStories();
    } catch (e: any) {
      setError(e.message || 'Failed to flag story');
    }
  };

  const handleExport = async () => {
    if (selectedIds.size === 0) return;
    try {
      await apiClient('/story-builder/stories/export', {
        method: 'POST',
        body: { storyIds: Array.from(selectedIds) },
      });
      setSelectedIds(new Set());
    } catch (e: any) {
      setError(e.message || 'Failed to export stories');
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div>
      {/* Filter bar */}
      <div style={styles.filterBar}>
        <input
          style={{ ...styles.input, minWidth: '140px' }}
          placeholder="Chapter"
          value={chapter}
          onChange={e => { setChapter(e.target.value); setPage(1); }}
          aria-label="Filter by chapter"
        />
        <input
          style={{ ...styles.input, minWidth: '140px' }}
          placeholder="Family name"
          value={familyName}
          onChange={e => { setFamilyName(e.target.value); setPage(1); }}
          aria-label="Filter by family name"
        />
        <input
          style={{ ...styles.input, width: '90px' }}
          placeholder="Year"
          value={reunionYear}
          onChange={e => { setReunionYear(e.target.value); setPage(1); }}
          aria-label="Filter by reunion year"
        />
        {isAdmin && (
          <select
            style={styles.select}
            value={status}
            onChange={e => { setStatus(e.target.value); setPage(1); }}
            aria-label="Filter by status"
          >
            <option value="">All statuses</option>
            {STORY_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        {isAdmin && selectedIds.size > 0 && (
          <button style={styles.btnPrimary} onClick={handleExport} aria-label="Export selected stories">
            📄 Export ({selectedIds.size})
          </button>
        )}
      </div>

      {error && <p style={styles.errorText}>{error}</p>}

      {/* Flag reason modal */}
      {flagTarget && (
        <div style={styles.card}>
          <p style={{ margin: '0 0 8px 0', fontWeight: 600, color: TEXT_DARK }}>Flag Story — Provide Reason</p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              style={{ ...styles.input, flex: 1 }}
              placeholder="Reason for flagging..."
              value={flagReason}
              onChange={e => setFlagReason(e.target.value)}
              aria-label="Flag reason"
            />
            <button style={styles.btnPrimary} onClick={submitFlag} disabled={!flagReason.trim()}>Submit</button>
            <button style={styles.btnSecondary} onClick={() => { setFlagTarget(null); setFlagReason(''); }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <p style={{ color: TEXT_MUTED, padding: '1rem' }}>Loading stories...</p>
      ) : stories.length === 0 ? (
        <p style={styles.emptyState}>No stories match the selected criteria.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                {isAdmin && <th style={styles.th}>
                  <input
                    type="checkbox"
                    onChange={e => {
                      if (e.target.checked) setSelectedIds(new Set(stories.map(s => s.id)));
                      else setSelectedIds(new Set());
                    }}
                    checked={selectedIds.size === stories.length && stories.length > 0}
                    aria-label="Select all stories"
                  />
                </th>}
                <th style={styles.th}>Title</th>
                <th style={styles.th}>Guest</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Chapter</th>
                <th style={styles.th}>Featured</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {stories.map(story => (
                <tr key={story.id}>
                  {isAdmin && <td style={styles.td}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(story.id)}
                      onChange={() => toggleSelect(story.id)}
                      aria-label={`Select ${story.title}`}
                    />
                  </td>}
                  <td style={styles.td}>{story.title}</td>
                  <td style={styles.td}>{story.guestName}</td>
                  <td style={styles.td}>
                    <StatusBadge status={story.status} colors={STATUS_BADGE_COLORS} />
                  </td>
                  <td style={styles.td}>{story.chapterAffiliation || '—'}</td>
                  <td style={styles.td}>{story.featured ? '⭐' : ''}</td>
                  <td style={styles.td}>
                    {story.approvedAt
                      ? new Date(story.approvedAt).toLocaleDateString()
                      : new Date(story.createdAt).toLocaleDateString()}
                  </td>
                  <td style={styles.td}>
                    <button
                      style={iconBtn}
                      onClick={() => handleFeatureToggle(story.id)}
                      title={story.featured ? 'Unfeature' : 'Feature'}
                      aria-label={`${story.featured ? 'Unfeature' : 'Feature'} ${story.title}`}
                    >
                      ⭐
                    </button>
                    {isAdmin && (
                      <>
                        <button style={iconBtn} onClick={() => handleModerate(story.id, 'approve')} title="Approve" aria-label={`Approve ${story.title}`}>✓</button>
                        <button style={iconBtn} onClick={() => handleRetranslate(story.id, story.title)} title="Retranslate" aria-label={`Retranslate ${story.title}`}>🔄</button>
                        <button style={iconBtn} onClick={() => handleModerate(story.id, 'flag')} title="Flag" aria-label={`Flag ${story.title}`}>🚩</button>
                        <button style={iconBtn} onClick={() => handleModerate(story.id, 'remove')} title="Remove" aria-label={`Remove ${story.title}`}>🗑️</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={styles.pagination}>
          <span>{total} stories total</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              style={styles.btnSecondary}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              ← Prev
            </button>
            <span style={{ padding: '8px 0' }}>Page {page} of {totalPages}</span>
            <button
              style={styles.btnSecondary}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Questions Sub-Tab ───────────────────────────────────────

function QuestionsTab() {
  const [questions, setQuestions] = useState<StoryQuestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Add form
  const [newText, setNewText] = useState('');
  const [newCategory, setNewCategory] = useState(QUESTION_CATEGORIES[0]);

  // Edit state
  const [editId, setEditId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [editCategory, setEditCategory] = useState('');

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiClient<StoryQuestion[]>('/story-builder/questions');
      setQuestions(res);
    } catch (e: any) {
      setError(e.message || 'Failed to load questions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  const activeCount = questions.filter(q => q.active).length;
  const categoryDist = QUESTION_CATEGORIES.map(cat => ({
    category: cat,
    count: questions.filter(q => q.category === cat && q.active).length,
  }));

  const handleAdd = async () => {
    if (newText.trim().length < 10 || newText.trim().length > 500) {
      setError('Question text must be between 10 and 500 characters.');
      return;
    }
    setError('');
    try {
      await apiClient('/story-builder/questions', {
        method: 'POST',
        body: { text: newText.trim(), category: newCategory },
      });
      setNewText('');
      setNewCategory(QUESTION_CATEGORIES[0]);
      fetchQuestions();
    } catch (e: any) {
      setError(e.message || 'Failed to add question');
    }
  };

  const handleUpdate = async (id: string) => {
    if (editText.trim().length < 10 || editText.trim().length > 500) {
      setError('Question text must be between 10 and 500 characters.');
      return;
    }
    setError('');
    try {
      await apiClient(`/story-builder/questions/${id}`, {
        method: 'PATCH',
        body: { text: editText.trim(), category: editCategory },
      });
      setEditId(null);
      fetchQuestions();
    } catch (e: any) {
      setError(e.message || 'Failed to update question');
    }
  };

  const handleDeactivate = async (id: string) => {
    if (!window.confirm('Deactivate this question? This may be rejected if it violates bank constraints.')) return;
    setError('');
    try {
      await apiClient(`/story-builder/questions/${id}`, { method: 'DELETE' });
      fetchQuestions();
    } catch (e: any) {
      setError(e.message || 'Cannot deactivate: constraint violation');
    }
  };

  const startEdit = (q: StoryQuestion) => {
    setEditId(q.id);
    setEditText(q.text);
    setEditCategory(q.category);
  };

  return (
    <div>
      {/* Stats */}
      <div style={{ ...styles.card, display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
        <div>
          <span style={styles.label}>Active Questions</span>
          <p style={{ margin: '4px 0 0', fontSize: '1.2rem', fontWeight: 700, color: TEXT_DARK }}>{activeCount} / {questions.length}</p>
        </div>
        <div style={{ flex: 1 }}>
          <span style={styles.label}>Category Distribution</span>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
            {categoryDist.map(cd => (
              <span key={cd.category} style={{ ...styles.badge, backgroundColor: '#e8f5e9', color: '#2e7d32' }}>
                {CATEGORY_LABELS[cd.category] || cd.category}: {cd.count}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Add Question Form */}
      <div style={styles.card}>
        <p style={{ margin: '0 0 8px 0', fontWeight: 600, color: TEXT_DARK }}>Add Question</p>
        <div style={styles.formRow}>
          <div style={{ ...styles.formField, flex: 1 }}>
            <label style={styles.label}>Question Text (10-500 chars)</label>
            <textarea
              style={styles.textarea}
              value={newText}
              onChange={e => setNewText(e.target.value)}
              maxLength={500}
              placeholder="Enter cultural or personal question..."
              aria-label="New question text"
            />
          </div>
          <div style={styles.formField}>
            <label style={styles.label}>Category</label>
            <select
              style={styles.select}
              value={newCategory}
              onChange={e => setNewCategory(e.target.value)}
              aria-label="Question category"
            >
              {QUESTION_CATEGORIES.map(c => (
                <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
              ))}
            </select>
          </div>
          <button style={styles.btnPrimary} onClick={handleAdd}>Add</button>
        </div>
      </div>

      {error && <p style={styles.errorText}>{error}</p>}

      {/* Questions Table */}
      {loading ? (
        <p style={{ color: TEXT_MUTED, padding: '1rem' }}>Loading questions...</p>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Text</th>
              <th style={styles.th}>Category</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {questions.map(q => (
              <tr key={q.id}>
                <td style={{ ...styles.td, maxWidth: '400px' }}>
                  {editId === q.id ? (
                    <textarea
                      style={{ ...styles.textarea, width: '100%' }}
                      value={editText}
                      onChange={e => setEditText(e.target.value)}
                      maxLength={500}
                      aria-label="Edit question text"
                    />
                  ) : (
                    q.text
                  )}
                </td>
                <td style={styles.td}>
                  {editId === q.id ? (
                    <select
                      style={styles.select}
                      value={editCategory}
                      onChange={e => setEditCategory(e.target.value)}
                      aria-label="Edit category"
                    >
                      {QUESTION_CATEGORIES.map(c => (
                        <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
                      ))}
                    </select>
                  ) : (
                    <span style={{ ...styles.badge, backgroundColor: '#e3f2fd', color: '#1565c0' }}>
                      {CATEGORY_LABELS[q.category] || q.category}
                    </span>
                  )}
                </td>
                <td style={styles.td}>
                  <span style={{ ...styles.badge, backgroundColor: q.active ? '#e8f5e9' : '#eceff1', color: q.active ? '#2e7d32' : '#546e7a' }}>
                    {q.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={styles.td}>
                  {editId === q.id ? (
                    <>
                      <button style={iconBtn} onClick={() => handleUpdate(q.id)} title="Save" aria-label="Save changes">💾</button>
                      <button style={iconBtn} onClick={() => setEditId(null)} title="Cancel" aria-label="Cancel edit">✕</button>
                    </>
                  ) : (
                    <>
                      <button style={iconBtn} onClick={() => startEdit(q)} title="Edit" aria-label={`Edit question: ${q.text.slice(0, 30)}`}>✏️</button>
                      {q.active && (
                        <button style={iconBtn} onClick={() => handleDeactivate(q.id)} title="Deactivate" aria-label={`Deactivate question: ${q.text.slice(0, 30)}`}>🗑️</button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Vocabulary Sub-Tab ──────────────────────────────────────

function VocabularyTab() {
  const [entries, setEntries] = useState<HeritageVocabulary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filters
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Add form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEntry, setNewEntry] = useState({
    englishTerm: '', krioTerm: '', definition: '', category: '',
    source: '', pronunciation: '', culturalNotes: '',
  });

  // Edit state
  const [editId, setEditId] = useState<string | null>(null);
  const [editEntry, setEditEntry] = useState({
    englishTerm: '', krioTerm: '', definition: '', category: '',
    source: '', pronunciation: '', culturalNotes: '', verificationStatus: '',
  });

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filterCategory) params.set('category', filterCategory);
      if (filterStatus) params.set('verificationStatus', filterStatus);
      const res = await apiClient<HeritageVocabulary[]>(`/story-builder/vocabulary?${params.toString()}`);
      setEntries(res);
    } catch (e: any) {
      setError(e.message || 'Failed to load vocabulary');
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterStatus]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleAdd = async () => {
    if (!newEntry.englishTerm.trim() || !newEntry.krioTerm.trim() || !newEntry.definition.trim() || !newEntry.category.trim() || !newEntry.source.trim()) {
      setError('English term, Krio term, definition, category, and source are required.');
      return;
    }
    setError('');
    try {
      await apiClient('/story-builder/vocabulary', {
        method: 'POST',
        body: {
          englishTerm: newEntry.englishTerm.trim(),
          krioTerm: newEntry.krioTerm.trim(),
          definition: newEntry.definition.trim(),
          category: newEntry.category.trim(),
          source: newEntry.source.trim(),
          pronunciation: newEntry.pronunciation.trim() || undefined,
          culturalNotes: newEntry.culturalNotes.trim() || undefined,
        },
      });
      setNewEntry({ englishTerm: '', krioTerm: '', definition: '', category: '', source: '', pronunciation: '', culturalNotes: '' });
      setShowAddForm(false);
      fetchEntries();
    } catch (e: any) {
      setError(e.message || 'Failed to add vocabulary entry');
    }
  };

  const handleUpdate = async (id: string) => {
    setError('');
    try {
      await apiClient(`/story-builder/vocabulary/${id}`, {
        method: 'PATCH',
        body: {
          englishTerm: editEntry.englishTerm.trim(),
          krioTerm: editEntry.krioTerm.trim(),
          definition: editEntry.definition.trim(),
          category: editEntry.category.trim(),
          source: editEntry.source.trim(),
          pronunciation: editEntry.pronunciation.trim() || undefined,
          culturalNotes: editEntry.culturalNotes.trim() || undefined,
          verificationStatus: editEntry.verificationStatus,
        },
      });
      setEditId(null);
      fetchEntries();
    } catch (e: any) {
      setError(e.message || 'Failed to update entry');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this vocabulary entry? This action cannot be undone.')) return;
    setError('');
    try {
      await apiClient(`/story-builder/vocabulary/${id}`, { method: 'DELETE' });
      fetchEntries();
    } catch (e: any) {
      setError(e.message || 'Failed to delete entry');
    }
  };

  const startEdit = (v: HeritageVocabulary) => {
    setEditId(v.id);
    setEditEntry({
      englishTerm: v.englishTerm,
      krioTerm: v.krioTerm,
      definition: v.definition,
      category: v.category,
      source: v.source,
      pronunciation: v.pronunciation || '',
      culturalNotes: v.culturalNotes || '',
      verificationStatus: v.verificationStatus,
    });
  };

  return (
    <div>
      {/* Filter bar */}
      <div style={styles.filterBar}>
        <input
          style={{ ...styles.input, minWidth: '160px' }}
          placeholder="Filter by category"
          value={filterCategory}
          onChange={e => setFilterCategory(e.target.value)}
          aria-label="Filter by category"
        />
        <select
          style={styles.select}
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          aria-label="Filter by verification status"
        >
          <option value="">All statuses</option>
          {VERIFICATION_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button
          style={styles.btnPrimary}
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancel' : '+ Add Entry'}
        </button>
      </div>

      {error && <p style={styles.errorText}>{error}</p>}

      {/* Add Entry Form */}
      {showAddForm && (
        <div style={styles.card}>
          <p style={{ margin: '0 0 8px 0', fontWeight: 600, color: TEXT_DARK }}>Add Vocabulary Entry</p>
          <div style={styles.formRow}>
            <div style={styles.formField}>
              <label style={styles.label}>English Term*</label>
              <input style={styles.input} value={newEntry.englishTerm} onChange={e => setNewEntry({ ...newEntry, englishTerm: e.target.value })} aria-label="English term" />
            </div>
            <div style={styles.formField}>
              <label style={styles.label}>Krio Term*</label>
              <input style={styles.input} value={newEntry.krioTerm} onChange={e => setNewEntry({ ...newEntry, krioTerm: e.target.value })} aria-label="Krio term" />
            </div>
            <div style={styles.formField}>
              <label style={styles.label}>Category*</label>
              <input style={styles.input} value={newEntry.category} onChange={e => setNewEntry({ ...newEntry, category: e.target.value })} aria-label="Category" />
            </div>
            <div style={styles.formField}>
              <label style={styles.label}>Source*</label>
              <input style={styles.input} value={newEntry.source} onChange={e => setNewEntry({ ...newEntry, source: e.target.value })} aria-label="Source" />
            </div>
          </div>
          <div style={styles.formRow}>
            <div style={{ ...styles.formField, flex: 1 }}>
              <label style={styles.label}>Definition*</label>
              <textarea style={styles.textarea} value={newEntry.definition} onChange={e => setNewEntry({ ...newEntry, definition: e.target.value })} aria-label="Definition" />
            </div>
          </div>
          <div style={styles.formRow}>
            <div style={styles.formField}>
              <label style={styles.label}>Pronunciation</label>
              <input style={styles.input} value={newEntry.pronunciation} onChange={e => setNewEntry({ ...newEntry, pronunciation: e.target.value })} aria-label="Pronunciation" />
            </div>
            <div style={{ ...styles.formField, flex: 1 }}>
              <label style={styles.label}>Cultural Notes</label>
              <input style={styles.input} value={newEntry.culturalNotes} onChange={e => setNewEntry({ ...newEntry, culturalNotes: e.target.value })} aria-label="Cultural notes" />
            </div>
            <button style={styles.btnPrimary} onClick={handleAdd}>Save Entry</button>
          </div>
        </div>
      )}

      {/* Vocabulary Table */}
      {loading ? (
        <p style={{ color: TEXT_MUTED, padding: '1rem' }}>Loading vocabulary...</p>
      ) : entries.length === 0 ? (
        <p style={styles.emptyState}>No vocabulary entries match the selected filters.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>English</th>
                <th style={styles.th}>Krio</th>
                <th style={styles.th}>Category</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(v => (
                <tr key={v.id}>
                  {editId === v.id ? (
                    <>
                      <td style={styles.td}>
                        <input style={{ ...styles.input, width: '100%' }} value={editEntry.englishTerm} onChange={e => setEditEntry({ ...editEntry, englishTerm: e.target.value })} aria-label="Edit English term" />
                      </td>
                      <td style={styles.td}>
                        <input style={{ ...styles.input, width: '100%' }} value={editEntry.krioTerm} onChange={e => setEditEntry({ ...editEntry, krioTerm: e.target.value })} aria-label="Edit Krio term" />
                      </td>
                      <td style={styles.td}>
                        <input style={{ ...styles.input, width: '100%' }} value={editEntry.category} onChange={e => setEditEntry({ ...editEntry, category: e.target.value })} aria-label="Edit category" />
                      </td>
                      <td style={styles.td}>
                        <select style={styles.select} value={editEntry.verificationStatus} onChange={e => setEditEntry({ ...editEntry, verificationStatus: e.target.value })} aria-label="Edit verification status">
                          {VERIFICATION_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td style={styles.td}>
                        <button style={iconBtn} onClick={() => handleUpdate(v.id)} title="Save" aria-label="Save changes">💾</button>
                        <button style={iconBtn} onClick={() => setEditId(null)} title="Cancel" aria-label="Cancel edit">✕</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={styles.td}>{v.englishTerm}</td>
                      <td style={styles.td}>{v.krioTerm}</td>
                      <td style={styles.td}>{v.category}</td>
                      <td style={styles.td}>
                        <StatusBadge status={v.verificationStatus} colors={VERIFICATION_BADGE_COLORS} />
                      </td>
                      <td style={styles.td}>
                        <button style={iconBtn} onClick={() => startEdit(v)} title="Edit" aria-label={`Edit ${v.englishTerm}`}>✏️</button>
                        <button style={iconBtn} onClick={() => handleDelete(v.id)} title="Delete" aria-label={`Delete ${v.englishTerm}`}>🗑️</button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main Admin Panel Page ───────────────────────────────────

export function StoryBuilderAdminPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';

  // Determine visible tabs based on role
  const SUB_VIEWS: { key: SubView; label: string }[] = [
    { key: 'stories', label: 'Stories' },
    ...(isAdmin ? [
      { key: 'questions' as SubView, label: 'Questions' },
      { key: 'vocabulary' as SubView, label: 'Vocabulary' },
    ] : []),
  ];

  const [activeView, setActiveView] = useState<SubView>('stories');

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>📖 Story Builder — Admin</h1>

      {/* Sub-tab navigation */}
      <nav style={styles.subNav} aria-label="Admin panel sections">
        {SUB_VIEWS.map((view) => (
          <button
            key={view.key}
            style={activeView === view.key ? styles.subTabActive : styles.subTab}
            onClick={() => setActiveView(view.key)}
            aria-current={activeView === view.key ? 'page' : undefined}
          >
            {view.label}
          </button>
        ))}
      </nav>

      {/* Tab content */}
      {activeView === 'stories' && <StoriesTab isAdmin={isAdmin} />}
      {activeView === 'questions' && isAdmin && <QuestionsTab />}
      {activeView === 'vocabulary' && isAdmin && <VocabularyTab />}
    </div>
  );
}
